<template>
  <transition
    name="unpin-slide"
    @after-leave="afterLeave"
  >
    <div v-if="!isLeaving" class="info-card" :class="{ expanded: expanded.appointment, collapsed: !expanded.appointment }">
      <div class="info-card-content">
      <div v-if="!expanded.appointment" class="info-card-content-col-group">
          <div v-if="appointmentDisplay" class="appointment-date">
            <p class="month">{{ appointmentDisplay.month }}</p>
            <p class="day">{{ appointmentDisplay.day }}</p>
            <p class="weekday">{{ appointmentDisplay.weekday }}</p>
          </div>
          <div v-else class="appointment-date">
            <p class="month">---</p>
            <p class="day">--</p>
            <p class="weekday">---</p>
          </div>
          <div class="appointment-details">
            <p class="detail-title">Appointment:</p>
            <p v-if="appointmentDisplay" class="detail-text">{{ appointmentDisplay.formattedDate }}</p>
            <p v-if="appointmentDisplay" class="detail-text">{{ appointmentDisplay.formattedTime }}</p>
            <p v-if="!appointmentDisplay" class="detail-text">No appointment data</p>
          </div>
        </div>  
        <div v-else class="info-card-content-col-group">
              <div v-if="appointmentDisplay" class="appointment-date">
                  <p class="month">{{ appointmentDisplay.month }}</p>
                  <p class="day">{{ appointmentDisplay.day }}</p>
                  <p class="weekday">{{ appointmentDisplay.weekday }}</p>
              </div>
              <div v-else class="appointment-date">
                  <p class="month">---</p>
                  <p class="day">--</p>
                  <p class="weekday">---</p>
              </div>
              <div class="appointment-details">
                  <div v-if="appointmentDisplay">
                    <p class="detail-title">Appointment:</p>
                    <p class="detail-text">{{ appointmentDisplay.formattedDate }}</p>
                    <p class="detail-text">{{ appointmentDisplay.formattedTime }}</p>
                    <p class="detail-title">Estimated Meeting Time:</p>
                    <p class="detail-text">{{ appointmentDisplay.duration }} mins</p>
                    <p class="detail-title">Meeting Agenda:</p>
                    <p class="detail-text">{{ appointmentDisplay.type }}</p>
                  </div>
                  <div v-else>
                    <p class="detail-text">No appointment data available</p>
                  </div>
              </div>
          </div>
          <div v-if="expanded.appointment" class="action-icons">
            <div v-if="!isCardPinned" class="action-icon" @click.stop="handlePinCard">
              <img src="@/assets/cardActions/pin.svg" alt="Unpin" />
            </div>
            <div v-if="isCardPinned" class="action-icon" @click.stop="startUnpin">
              <img src="@/assets/cardActions/unpin.svg" alt="Pin" />
            </div>
            <div v-if="showFooter" class="action-icon" @click.stop="toggleCard('appointment')">
              <img src="@/assets/cardActions/collapse.svg" alt="Collapse" />
            </div>
          </div>
          <div v-else class="flex flex-row items-center gap-2">
            <div class="action-icon" @click.stop="toggleCard('appointment')">
              <img src="@/assets/cardActions/expand.svg" alt="Expand" />
            </div>
            <div class="drag-handle action-icon">
              <img src="@/assets/cardActions/move.svg" alt="Move" />
            </div>
          </div>
        </div>
        <div class="card-expand" :class="{ 'justify-end': !showFooter, 'justify-between': showFooter }">
          <FoldableButton v-if="showFooter" customized_class="drag-handle" text="Move">
            <template #prefix>
              <img src="@/assets/cardActions/move.svg" alt="Move" />
            </template>
          </FoldableButton>
          <div class="flex flex-row gap-1 justify-end">
            <FoldableButton text="Edit" @click="handleEditAppointment">
              <template #prefix>
                <img src="@/assets/cardActions/edit.svg" alt="Edit" />
              </template>
            </FoldableButton>
          </div>
        </div>
      </div>
      </transition>
 
</template>
<script setup>
import { ref, computed } from 'vue'
import { getCurrentPatientId } from '@/utils/patientUtils';
import { useCardActions } from '@/composables/useCardActions';
import { useMyPatientsStore } from '@/store/helpers';


// Props - can accept either a single appointment or patient data
const props = defineProps({
  patient: {
    type: String,
    default: ''
  },
  appointment: {
    type: Object,
    default: () => ({})
  },
  cardData: {
    type: Object,
    default: () => ({})
  },
  showFooter: {
    type: Boolean,
    default: true
  },
  context: {
    type: String,
    default: 'card'
  }
})



// Define emits
const emit = defineEmits(['unpin-card', 'pin-card', 'edit-appointment']);

const { isCardPinned, handlePinCard, handleUnpinCard } = useCardActions(props, emit);

const { patientInfoStore } = useMyPatientsStore();

const isLeaving = ref(false);

const startUnpin = () => {
  if(props.showFooter){
    isLeaving.value = true; // triggers the leave animation
  } else {
    handleUnpinCard()
  }
};


// This is called after the leave animation finishes
const afterLeave = () => {
  // For tab cards, find card_id using getPegboardCardId
  let cardDataToUnpin = props.cardData;
  if (props.context === 'card' && appointmentData.value?.event_id) {
    const cardData = {
      category: 'Calendar',
      entity_type: 'appointment',
      entity_id: String(appointmentData.value.event_id)
    };
    const cardId = patientInfoStore.getters.getPegboardCardId(cardData);
    if (cardId) {
      cardDataToUnpin = { ...cardData, id: cardId };
    }
  }
  emit('unpin-card', cardDataToUnpin);
};


const expanded = ref({
      appointment: true,
    })

const toggleCard = (cardType) => {
      expanded.value[cardType] = !expanded.value[cardType]
    }



// Handle edit appointment click
const handleEditAppointment = () => {
  const appointment = appointmentData.value
  if (appointment) {
    emit('edit-appointment', appointment)
  }
}

// Get appointment data directly from store - returns appointment object or null
const appointmentData = computed(() => {
  // For tab cards: use appointment prop directly
  if (props.appointment && props.context === 'card') {
    return props.appointment
  }
  
  // For pegboard cards: fetch from store using entity_id
  if (props.cardData?.entity_id) {
    const appointments = patientInfoStore.state.currentPatientAppointments.value || []
    
    const numericEventId = Number(props.cardData.entity_id)
    const appointment = appointments.find(apt => 
      apt.event_id === numericEventId || 
      apt.id === numericEventId
    )
    
    if (appointment) {
      return appointment
    } else {
      return null
    }
  }
  
  // Fallback
  const cardData = patientInfoStore.getters.getAppointmentCardData?.value
  return cardData?.nextAppointment || null
})

// Process the appointment data for display
const appointmentDisplay = computed(() => {
  // appointmentData now returns the appointment object directly
  const appointment = appointmentData.value
  
  if (!appointment) {
    return null
  }
  
  // Parse start_time from original API format
  const startDate = new Date(appointment.start_time)
  const endDate = new Date(appointment.end_time)
  
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  
  // Calculate duration in minutes
  const duration = Math.round((endDate - startDate) / 60000)
  
  // Format date (MMM-DD-YYYY)
  const formattedDate = `${months[startDate.getMonth()]}-${String(startDate.getDate()).padStart(2, '0')}-${startDate.getFullYear()}`
  
  // Format time (e.g., "10:00 AM - 11:00 AM")
  const formatTime = (date) => {
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'PM' : 'AM'
    const displayHours = hours % 12 || 12
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${ampm}`
  }
  
  const formattedTime = `${formatTime(startDate)} - ${formatTime(endDate)}`
  
  return {
    // Original API fields
    ...appointment,
    // Display fields
    month: months[startDate.getMonth()],
    day: startDate.getDate(),
    weekday: days[startDate.getDay()],
    formattedDate: formattedDate,
    formattedTime: formattedTime,
    duration: duration,
    // Map API fields to expected names for compatibility
    title: appointment.event_title,
    type: appointment.event_type || 'Appointment',
    date: appointment.start_time
  }
})

</script>


<style scoped>


/* Expanded card footer visibility with hover expand animation */
.card-expand {
  display: flex;
  align-items: center;
  gap: 16px;
  /* animation base state */
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  padding-top: 0;
  margin-top: 0;
  border-top: 1px dashed var(--DividerGray, #C4C4C4);
  border-top-color: transparent;
  pointer-events: none;
  transition: max-height 300ms linear,
              opacity 300ms linear,
              padding-top 300ms linear,
              margin-top 300ms linear,
              border-top-color 300ms linear;
}

.info-card.expanded:hover .card-expand {
  /* ensure enough room for actions */
  max-height: 80px;
  opacity: 1;
  padding-top: 10px;
  margin-top: 10px;
  border-top-color: var(--DividerGray, #C4C4C4);
  pointer-events: auto;
}

.pin-toggle {
  position: relative;
  cursor: pointer;
}

.pin-toggle .icon {
  position: absolute;
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.pin-toggle .unpin {
  opacity: 0;
  transform: scale(0.8);
}

/* Hover effect */
.pin-toggle:hover .pin {
  opacity: 0;
  transform: scale(1);
}

.pin-toggle:hover .unpin {
  opacity: 1;
  transform: scale(1);
}


.unpin-slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), 
              opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.unpin-slide-leave-to {
  transform: translateX(-100%) scale(0.95);
  opacity: 0;
}

.peg-board-title {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 22px;
  color: #ffffff;
  text-align: center;
  margin-bottom: 16px;
}

.peg-board-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  padding: 0 8px;
}

.info-card {
  background-color: var(--cool-white, #ffffff);
  border-radius: 5px;
  padding: 12px 16px;
  box-shadow: 0px 1px 15px 0px rgba(74, 74, 74, 0.15);
}

.info-card-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.info-card-content-col-group {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
}

.info-row-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-grow: 1;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon-container {
  background-color: #f2f2f2;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.icon-style {
  width: 20px;
  height: 20px;
}

.info-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #333333;
  letter-spacing: 0.25px;
  line-height: 20px;
}

.action-icons {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.action-icon {
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.appointment-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 50px;
  height: 64px;
  border-radius: 5px;
  background-color: #ffffff;
  box-shadow: 0px 1px 15px 0px rgba(74, 74, 74, 0.1);
  overflow: hidden;
}

.month {
  background-color: #004879;
  color: white;
  width: 100%;
  text-align: center;
  padding: 2px 7px;
  font-size: 12px;
  font-family: 'Lato', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  line-height: 18px;
}

.day {
  color: rgba(0, 0, 0, 0.65);
  text-align: center;
  font-family: 'Lato', sans-serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  height: 24px;
}

.weekday {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  color: rgba(0, 0, 0, 0.65);
  line-height: 14px;
  padding-bottom: 4px;
  font-weight: 400;
}

.appointment-details {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    justify-content: center;
}

.detail-title {
    font-family: 'Inter', sans-serif;
    font-size: 10px;
    color: var(--secondaryblack, #666);
    font-weight: 400;
    letter-spacing: 0.1px;
    line-height: 14px;
}
.detail-text {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: var(--trueblack, #333);
    font-weight: 400;
    letter-spacing: 0.25px;
    line-height: 20px;
    margin-bottom: 0;
}
.action-icons-appointment {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-top: 20px;
}
.info-text-title {
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #666;
}
.info-text-large {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    color: #333;
    font-weight: 500;
}

/* Badge for billing preference */
.badge {
  color: #25B8C2;
  font-family: Inter;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px; /* 120% */
  letter-spacing: 0.25px;
  display: flex;
  padding: 4px 8px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.badge-default {
  border-radius: 20px;
  background: #EBFFF8;
  width: 68px;
}

.expand-action {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1B79A0;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 5px;
}

.expand-action span {
  color: var(--color-Darkblue-000, #1E4670);
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}

/* Icon-only buttons with hover text reveal */
.edit-expand,
.move-expand {
  display: flex;
  align-items: center;
  width: 26px;
  overflow: hidden;
  padding: 4px;
  justify-content: center;
  transition: width 0.25s ease, padding 0.25s ease, gap 0.25s ease;
  gap: 0;
}

.edit-expand:hover,
.move-expand:hover {
  padding: 4px 10px;
  justify-content: flex-start;
  gap: 6px;
}

.edit-expand:hover { width: 74px; }
.move-expand:hover { width: 78px; }

.edit-expand span,
.move-expand span {
  white-space: nowrap;
  opacity: 0;
  min-width: 0;
  transition: opacity 0.2s ease;
  transition-delay: 0.05s;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  color: #1E4670;
  text-decoration: underline;
}

.edit-expand:hover span,
.move-expand:hover span {
  opacity: 1;
}

.edit-expand img,
.move-expand img {
  flex-shrink: 0;
}

.action-icon {
  width: 28px;          /* w-7 */
  height: 28px;         /* h-7 */
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 5px;   /* rounded-[5px] */
  padding: 5px;         /* pd-5 (assuming you meant padding) */
  cursor: pointer;
  transition: background-color 0.2s; /* duration-200 transition-colors */
}

.action-icon:hover,
.expand-action:hover {
  background-color: #E5F1FB; /* hover:bg-[#E5F1FB] */
}
</style>
